/**
 * Notificaciones con la app cerrada (Web Push). El servidor solo guarda en
 * qué momentos dar el toque a este móvil; nunca ve el presupuesto. Los
 * textos los prepara la app y los deja en IndexedDB, donde los lee el
 * service worker (`public/avisos-sw.js`) al llegar el toque.
 */
import { send } from '@actual-app/core/platform/client/connection';

import { hayPendiente, momentoPendiente, toquesSiguientes } from './avisos';
import type { AvisosGavi, Recuento, TextosAvisos } from './avisos';

const CLAVE_DISPOSITIVO = 'rumbo:avisos:dispositivo';
const CLAVE_RECUENTO = 'rumbo:avisos:recuento';
export const EVENTO_RECUENTO = 'rumbo-avisos-recuento';

const servidor: { url: string | null; token: string | null } = {
  url: null,
  token: null,
};

function leerLocal(clave: string): string | null {
  try {
    return window.localStorage.getItem(clave);
  } catch {
    return null;
  }
}

function escribirLocal(clave: string, valor: string | null) {
  try {
    if (valor === null) {
      window.localStorage.removeItem(clave);
    } else {
      window.localStorage.setItem(clave, valor);
    }
  } catch {
    // Sin almacenamiento (modo privado): sin notificaciones.
  }
}

/** Id de este móvil para el servidor (no dice nada de la persona). */
function idDispositivo(crear: boolean): string | null {
  let id = leerLocal(CLAVE_DISPOSITIVO);
  if (!id && crear) {
    id = crypto.randomUUID();
    escribirLocal(CLAVE_DISPOSITIVO, id);
  }
  return id;
}

/** Lo que quedaba por hacer la última vez que se vio Inicio. */
export function leerRecuento(): Recuento | null {
  try {
    const r = JSON.parse(leerLocal(CLAVE_RECUENTO) ?? 'null');
    return r && typeof r.cuando === 'number' ? (r as Recuento) : null;
  } catch {
    return null;
  }
}

/** Guarda el recuento (solo en este móvil) si ha cambiado. */
export function guardarRecuento(r: Omit<Recuento, 'cuando'>) {
  const antes = leerRecuento();
  if (
    antes &&
    antes.sinCategoria === r.sinCategoria &&
    antes.enRojo === r.enRojo &&
    antes.porRevisar === r.porRevisar &&
    (antes.nombreEnRojo ?? null) === (r.nombreEnRojo ?? null)
  ) {
    return;
  }
  escribirLocal(CLAVE_RECUENTO, JSON.stringify({ ...r, cuando: Date.now() }));
  window.dispatchEvent(new Event(EVENTO_RECUENTO));
}

// --- IndexedDB (la misma base que abre public/avisos-sw.js) ---------------

function abrirBase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const peticion = indexedDB.open('rumbo-avisos', 1);
    peticion.onupgradeneeded = () => peticion.result.createObjectStore('kv');
    peticion.onsuccess = () => resolve(peticion.result);
    peticion.onerror = () => reject(peticion.error);
  });
}

/** Deja los textos listos para el service worker. */
export async function escribirTextos(textos: TextosAvisos) {
  if (typeof indexedDB === 'undefined') {
    return;
  }
  try {
    const base = await abrirBase();
    await new Promise<void>((resolve, reject) => {
      const tx = base.transaction('kv', 'readwrite');
      tx.objectStore('kv').put(textos, 'textos');
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    base.close();
  } catch {
    // Sin textos, el service worker usa unos genéricos.
  }
}

/** Un aviso pulsado hace más de esto ya no lleva a ningún sitio. */
const VIGENCIA_RUTA_MS = 10 * 60 * 1000;

/**
 * La pantalla que pidió abrir el último aviso pulsado (la deja
 * public/avisos-sw.js en la clave `ir-a`). Se borra al leerla.
 */
export async function tomarRutaAviso(): Promise<string | null> {
  if (typeof indexedDB === 'undefined') {
    return null;
  }
  try {
    const base = await abrirBase();
    const valor = await new Promise<unknown>((resolve, reject) => {
      const tx = base.transaction('kv', 'readwrite');
      const almacen = tx.objectStore('kv');
      const get = almacen.get('ir-a');
      get.onsuccess = () => {
        if (get.result != null) {
          almacen.delete('ir-a');
        }
      };
      tx.oncomplete = () => resolve(get.result);
      tx.onerror = () => reject(tx.error);
    });
    base.close();
    return rutaVigente(valor, Date.now());
  } catch {
    return null;
  }
}

export function rutaVigente(valor: unknown, ahora: number): string | null {
  if (typeof valor !== 'object' || valor == null) {
    return null;
  }
  const { ruta, cuando } = valor as { ruta?: unknown; cuando?: unknown };
  if (
    typeof ruta !== 'string' ||
    !ruta.startsWith('/') ||
    ruta.startsWith('//') ||
    typeof cuando !== 'number' ||
    ahora - cuando > VIGENCIA_RUTA_MS ||
    cuando > ahora + 60_000
  ) {
    return null;
  }
  return ruta;
}

// --- Servidor ---------------------------------------------------------------

async function conexion() {
  if (!servidor.url || !servidor.token) {
    try {
      const { url, token } = await send('rumbo-uso-conexion');
      servidor.url = url;
      servidor.token = token;
    } catch {
      // Sin servidor (solo en este dispositivo): sin notificaciones cerradas.
    }
  }
  return servidor.url && servidor.token
    ? { url: servidor.url, token: servidor.token }
    : null;
}

async function pedir(
  ruta: string,
  metodo: 'GET' | 'PUT' | 'POST' | 'DELETE',
  cuerpo?: unknown,
  keepalive = false,
) {
  const c = await conexion();
  if (!c) {
    return null;
  }
  const res = await fetch(`${c.url}/avisos${ruta}`, {
    method: metodo,
    keepalive,
    headers: {
      'X-ACTUAL-TOKEN': c.token,
      ...(cuerpo ? { 'Content-Type': 'application/json' } : {}),
    },
    body: cuerpo ? JSON.stringify(cuerpo) : undefined,
  });
  if (!res.ok) {
    throw new Error(`avisos ${metodo} ${res.status}`);
  }
  return res.json();
}

function aBytes(base64url: string): Uint8Array<ArrayBuffer> {
  const relleno = '='.repeat((4 - (base64url.length % 4)) % 4);
  const b64 = (base64url + relleno).replace(/-/g, '+').replace(/_/g, '/');
  const texto = atob(b64);
  const bytes = new Uint8Array(new ArrayBuffer(texto.length));
  for (let i = 0; i < texto.length; i++) {
    bytes[i] = texto.charCodeAt(i);
  }
  return bytes;
}

async function registroSW(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !navigator.serviceWorker) {
    return null;
  }
  return (await navigator.serviceWorker.getRegistration()) ?? null;
}

/**
 * Pone al día lo que sabe el servidor de este móvil: los toques de los
 * próximos 45 días o, si están apagados o sin permiso, que no hay ninguno.
 */
export async function sincronizarAvisos(
  c: AvisosGavi,
  textos: TextosAvisos,
  /** Presupuestos compartidos de los que se quiere el aviso de la pareja. */
  pareja: string[] = [],
) {
  await escribirTextos(textos);
  const permitido =
    typeof Notification !== 'undefined' &&
    Notification.permission === 'granted';
  const reg = await registroSW();
  const conPareja = c.pareja && pareja.length > 0;
  if ((!c.activo && !conPareja) || !permitido || !reg?.pushManager) {
    const id = idDispositivo(false);
    if (id) {
      try {
        await pedir(`/${id}`, 'DELETE');
        escribirLocal(CLAVE_DISPOSITIVO, null);
      } catch {
        // Ya se intentará la próxima vez.
      }
    }
    return;
  }
  try {
    const respuesta = await pedir('/clave', 'GET');
    const clave: string | undefined = respuesta?.data?.clave;
    if (!clave) {
      return;
    }
    let suscripcion = await reg.pushManager.getSubscription();
    if (!suscripcion) {
      suscripcion = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: aBytes(clave),
      });
    }
    const id = idDispositivo(true);
    await pedir(`/${id}`, 'PUT', {
      suscripcion: suscripcion.toJSON(),
      toques: c.activo
        ? toquesSiguientes(c, new Date()).map(d => d.toISOString())
        : [],
      pareja: conPareja ? pareja : [],
    });
  } catch {
    // Servidor antiguo, sin red o el navegador no deja: sin toques cerrados.
  }
}

/**
 * Al salir de la app con algo pendiente (lo que la persona eligió), pide al
 * servidor un toque más tarde; al volver, lo quita.
 */
export function avisarAlSalir(c: AvisosGavi, saliendo: boolean) {
  const id = idDispositivo(false);
  if (!id || !c.activo) {
    return;
  }
  const en =
    saliendo && hayPendiente(c, leerRecuento())
      ? momentoPendiente(c, new Date()).toISOString()
      : null;
  pedir(`/${id}/pendiente`, 'PUT', { en }, true).catch(() => {
    // Sin red al salir: ese toque no llegará.
  });
}

/**
 * Pareja: acabas de apuntar en un presupuesto compartido. El servidor avisa
 * a quien lo pidió (solo recibe el id del presupuesto).
 */
export function avisarApunte(fileId: string) {
  pedir('/pareja/apunte', 'POST', { fileId }, true).catch(() => {
    // Sin red o servidor antiguo: esta vez no hay aviso.
  });
}
